import React, { useState, useEffect, useRef } from "react";
import { hydrateRoot, createRoot } from "react-dom/client";
import "rc-slider/assets/index.css";
import "./styles/index.scss";
import "./index.css";
import "./fonts/line-awesome-1.3.0/css/line-awesome.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Toaster, toast } from "react-hot-toast";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { DataProvider } from "data/data-provider";
import { globalJson } from "global/global_json";
import serverURL from "api/auth/auth";
import { setPostHogCompany } from "analytics/posthog";

// Scope all PostHog events to this tenant (DFW's company id is static, unlike
// Template's runtime-resolved tenant) before the first $pageview can fire.
setPostHogCompany(globalJson?.prod_company_id);

// Enhanced detection for react-snap and pre-rendering environments
const isPrerendering =
  typeof navigator !== "undefined" &&
  (navigator.userAgent.includes("jsdom") ||
    navigator.userAgent.includes("ReactSnap") ||
    navigator.userAgent === "ReactSnap" ||
    (window.navigator && window.navigator.userAgent.includes("jsdom")));

// Check if we're in a react-snap pre-rendering context
const isReactSnapRendering =
  typeof window !== "undefined" && window.navigator.userAgent === "ReactSnap";

// Improved check for first render and hydration
const isHydrating = () => {
  if (typeof window === "undefined") return true;
  return (
    document.readyState !== "complete" ||
    typeof window.__REACT_QUERY_HYDRATION__ !== "undefined" ||
    window.__INIT_COMPLETED__ !== true
  );
};

// Global flag to track initial render completion
if (typeof window !== "undefined") {
  // Initialize the property to avoid undefined access
  window.__INIT_COMPLETED__ = false;

  // Set the flag after a short delay to ensure hydration is complete
  setTimeout(() => {
    window.__INIT_COMPLETED__ = true;
  }, 1000);
}

async function fetchStripeKey(companyId: number): Promise<string | null> {
  // Enhanced check to prevent API calls during any initialization phase
  if (
    isPrerendering ||
    document.readyState !== "complete" ||
    typeof window === "undefined" ||
    window.__INIT_COMPLETED__ !== true
  ) {
    return null;
  }

  try {
    console.log("Fetching Stripe key for companyId:", companyId);

    const response = await fetch(
      `${serverURL}/api/payment/publishable-key/?company_id=${companyId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    console.log("API response:", response);

    const contentType = response.headers.get("content-type");
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to fetch Stripe key. Status:", response.status);
      console.error("Response Error Body:", errorText);
      return null;
    }

    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();
      console.log("Full API Response JSON:", data);

      if (data.publishable_key) {
        console.log("Fetched Stripe key:", data.publishable_key);
        return data.publishable_key;
      } else {
        console.error("publishable_key is undefined in the API response.");
        return null;
      }
    } else {
      const errorText = await response.text();
      console.error("Unexpected content type:", contentType);
      console.error("Response Error Body:", errorText);
      return null;
    }
  } catch (error) {
    console.error("Error fetching Stripe key:", error);
    return null;
  }
}

const rootElement = document.getElementById("root") as HTMLElement;

const RootComponent = () => {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  // Never show loading during pre-rendering to ensure react-snap captures content
  const [loading, setLoading] = useState(
    isPrerendering || isReactSnapRendering ? false : true,
  );
  const [retryCount, setRetryCount] = useState(0);
  const [isFullyInitialized, setIsFullyInitialized] = useState(
    isPrerendering || isReactSnapRendering,
  );
  const maxRetries = 3;
  const hasMountedRef = useRef(isPrerendering || isReactSnapRendering);

  // Set up a timer to mark the component as fully mounted
  useEffect(() => {
    // Skip all initialization during pre-rendering
    if (isPrerendering || isReactSnapRendering) {
      hasMountedRef.current = true;
      setIsFullyInitialized(true);
      return;
    }

    const timer = setTimeout(() => {
      hasMountedRef.current = true;
      setIsFullyInitialized(true);
    }, 1500); // Wait 1.5s after mount before considering fully initialized

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Skip Stripe initialization entirely during pre-rendering
    if (isPrerendering || isReactSnapRendering) {
      setLoading(false);
      setStripePromise(null);
      return;
    }

    // Don't try to initialize until the DOM is fully loaded
    if (document.readyState !== "complete") {
      const handleLoad = () => {
        // Delay to ensure React is done with initial rendering
        setTimeout(() => setRetryCount((c) => c + 1), 500);
      };

      window.addEventListener("load", handleLoad, { once: true });
      return () => window.removeEventListener("load", handleLoad);
    }

    // Delay initialization to ensure the app is properly mounted
    const delay = retryCount > 0 ? 1000 * retryCount : 300;

    const initializeStripe = async () => {
      const companyId = globalJson?.prod_company_id;

      if (!companyId) {
        console.error("Company ID is not available.");
        setStripePromise(null);
        setLoading(false);
        return;
      }

      try {
        // Additional safety check - don't make API calls until fully initialized
        if (!hasMountedRef.current || !isFullyInitialized) {
          setLoading(false);
          return;
        }

        const stripeKey = await fetchStripeKey(companyId);
        if (stripeKey) {
          setStripePromise(loadStripe(stripeKey));
        } else if (retryCount < maxRetries) {
          // Retry logic
          setRetryCount((prevCount) => prevCount + 1);
          return; // Will trigger re-render and this effect will run again
        } else {
          console.warn("Failed to load Stripe key after retries");
          setStripePromise(null);

          // Only show error if we're fully initialized
          if (
            isFullyInitialized &&
            hasMountedRef.current &&
            document.readyState === "complete"
          ) {
            // Delay the toast to ensure UI is stable
            setTimeout(() => {
              toast.error(
                "Error loading Stripe. Some payment features may be unavailable.",
                { duration: 4000, id: "stripe-error" },
              );
            }, 500);
          }
        }
      } catch (error) {
        console.error("Error during Stripe initialization:", error);
        setStripePromise(null);
      } finally {
        setLoading(false);
      }
    };

    // Delay initialization if we're retrying
    const timeoutId = setTimeout(initializeStripe, delay);
    return () => clearTimeout(timeoutId);
  }, [retryCount, isFullyInitialized]);

  // Don't show loading spinner during pre-rendering to ensure content is captured
  if (loading && !isPrerendering && !isReactSnapRendering) {
    return (
      <div className="flex justify-center items-center col-span-full bg-black min-h-[100vh]">
        <div className="spinner-border animate-spin inline-block w-6 h-6 border-2 rounded-full border-t-transparent border-white0"></div>
      </div>
    );
  }

  const appContent = (
    <GoogleOAuthProvider clientId="792199944204-3u7t512ta8hbrhptdjomhqp2nb7jh41h.apps.googleusercontent.com">
      <DataProvider>
        <Elements stripe={stripePromise}>
          <App />
        </Elements>
        <Toaster
          containerStyle={{ zIndex: 999999 }}
          toastOptions={{
            style: { zIndex: 999999 },
            className: "toast-notification",
          }}
        />
      </DataProvider>
    </GoogleOAuthProvider>
  );

  // Wrap in StrictMode only for development to catch potential issues
  return process.env.NODE_ENV === "development" ? (
    <React.StrictMode>{appContent}</React.StrictMode>
  ) : (
    appContent
  );
};

// For react-snap compatibility
// This helps React-Snap know when hydration is complete
if (typeof window !== "undefined") {
  // Original snapSaveState functionality
  const originalSnapSaveState = window.snapSaveState;

  // Override the function to also set our init flag
  window.snapSaveState = function () {
    window.__INIT_COMPLETED__ = true;
    // Call original if it exists
    if (originalSnapSaveState) {
      originalSnapSaveState.call(window);
    }
  };
}

// Use hydrateRoot for production (react-snap), createRoot for development
// Check if root element has pre-rendered content
const hasPrerenderedContent = rootElement.hasChildNodes();
const isDevelopment = process.env.NODE_ENV === "development";

if (isDevelopment || !hasPrerenderedContent) {
  // Use createRoot for development to enable Fast Refresh
  const root = createRoot(rootElement);
  root.render(<RootComponent />);
} else {
  // Use hydrateRoot for production with react-snap pre-rendered content
  hydrateRoot(rootElement, <RootComponent />);
}

// Delay the reportWebVitals to ensure the app has time to fully initialize
setTimeout(() => {
  reportWebVitals();
}, 2000);
